import moment from "moment-timezone";
import routes from "routes/routes";
import { DISTRICT_ALL } from "modules/bookLocation/common/url/bookingQuerySchema";
import { stringifyBookingQuery } from "modules/bookLocation/common/url/bookingQuery";
import type { BookingQuery } from "modules/bookLocation/common/url/bookingQuerySchema";
import type { RoomWeekResponse } from "api/roomWeek/roomWeekApiSlice";
import { DEFAULT_TIME_ZONE } from "./weekGrid";
import { spaceOf } from "../../util/useCases";
import { SERVICE_ENTRY_ROUTE } from "modules/home/serviceEntryRoutes";
import type { TenantConfigService } from "types/tenantConfig";

/**
 * Where a click on a free slot leads — the existing room flow, prefilled
 * (M3, #3036).
 *
 * The flow has no way in by room id yet: it replays its own search from the
 * URL, and `objectId` selects the room once that search returns it. So the
 * link opens the flow's FIRST step with location, day, time and room filled
 * in, and the person adds what only they can answer (event type, number of
 * guests) before the search runs. Opening a later step would skip the event
 * type, which venue booking creation requires.
 *
 * The link also carries the opened room's product category (#3035), so the
 * flow is the one that category configures: for an object-first category it
 * opens straight on the time step for this room, otherwise on the usual first
 * step narrowed to the category, as entering from its home tile does. Only for
 * the opened room itself: a segment of a divided hall can be a product of its
 * own, filed elsewhere, and a wrong key would narrow the search away from it.
 *
 * `carry` is what the customer already entered in the flow before opening
 * this week (head count, event, #3069) — carried on so it is not asked twice.
 *
 * Venue books by the hour; an apartment books whole days, so its link carries
 * the day and the next day as check-out, and no times. An hourly room opens its
 * own use case's entry: `/venue/book` for a venue, `/hall/book` for a sports
 * hall, which runs the same flow.
 */
export const bookingLinkFor = (
  data: RoomWeekResponse,
  target: { groupId: string },
  start: Date,
  end: Date,
  timeZone: string = DEFAULT_TIME_ZONE,
  carry: Pick<BookingQuery, "numberOfGuests" | "eventType"> = {}
): string => {
  const s = moment.tz(start, timeZone);
  const e = moment.tz(end, timeZone);
  const base: BookingQuery = {
    ...carry,
    city: data.location.city ?? undefined,
    district: DISTRICT_ALL,
    objectId: target.groupId,
    step: 0,
  };
  if (data.categoryKey && target.groupId === data.groupId) {
    base.category = data.categoryKey;
  }
  if (spaceOf(data.useCase)?.timeUnit === "night") {
    const query = stringifyBookingQuery({
      ...base,
      checkInDate: s.format("YYYY-MM-DD"),
      checkOutDate: s.clone().add(1, "day").format("YYYY-MM-DD"),
    });
    return `${routes.roomRentalPath}?${query}`;
  }
  // A slot ending at midnight ends on the NEXT day at 0:00 — across days any
  // end time is valid in the flow, while "24:00" is not a time it accepts.
  const query = stringifyBookingQuery({
    ...base,
    checkInDate: s.format("YYYY-MM-DD"),
    checkOutDate: e.format("YYYY-MM-DD"),
    startTime: s.format("H:mm"),
    endTime: e.format("H:mm"),
  });
  const entry = SERVICE_ENTRY_ROUTE[data.useCase as TenantConfigService] ?? routes.venueReservationPath;
  return `${entry}?${query}`;
};
